/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import useUrlQuery from 'Hooks/useUrlQuery';
import { paymentToken, validPayment } from 'Services/Applications/Payment';
import { useConfig } from 'Src/contexts/Config';
import { VALID_PAYMENT } from 'Utils/queries';

interface UseValidPaymentQueryFnOptions {
  payment_id: string;
  payment_intent_id: string;
  useSignature?: boolean;
}

const getSignature = async (
  payment_intent_id: string | undefined,
  useSignature: boolean,
) => {
  if (!payment_intent_id || !useSignature) return undefined;
  try {
    const tokenResponse = await paymentToken(payment_intent_id);
    return tokenResponse.data.token as string;
  } catch {
    return undefined;
  }
};

export async function useValidPaymentQueryFn({
  payment_id,
  payment_intent_id,
  useSignature = false,
}: UseValidPaymentQueryFnOptions) {
  if (!payment_id && !payment_intent_id) return null;
  const paymentPayload = payment_id ? { payment_id } : { payment_intent_id };
  const signature = await getSignature(payment_intent_id, useSignature);
  const { data: resp } = await validPayment(paymentPayload, signature);

  return {
    amount: resp.amount,
    callback: resp.callback,
    cancellationToken: resp.cancellation_token,
    integrationName: resp.integration_name,
    merchantId: resp.merchant_id,
    merchantLogo: resp.merchant_logo,
    merchantName: resp.merchant_name,
    orderId: resp.order_id,
    paymentId: payment_id,
    paymentIntentId: payment_intent_id,
    valid: resp.valid,
  };
}

export default function useValidPayment() {
  const { payment_id, payment_intent_id } = useUrlQuery();
  const {
    features: {
      VALIDATE_PAYMENT_WITH_TOKEN: useSignature,
    },
  } = useConfig();

  const { data, ...rest } = useQuery({
    queryFn: () => useValidPaymentQueryFn({ payment_id, payment_intent_id, useSignature }),
    queryKey: [VALID_PAYMENT, payment_id, payment_intent_id],
  });

  return { payment: data, ...rest };
}
